import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Copy, Check, Share2, Loader2 } from "lucide-react";
import { loyaltyReferral } from "../../lib/api";
import { tr } from "../../lib/i18n";

export const REF_KEY = "loyalty_ref";
export const REF_PATHS = ["/fidelity", "/fidelity-app"];

const L = {
  title: { it: "Invita un amico", en: "Invite a friend", es: "Invita a un amigo", fr: "Invitez un ami", de: "Freund einladen" },
  sub: { it: "5€ per te, 10€ di benvenuto per lui", en: "€5 for you, €10 welcome for them", es: "5€ para ti, 10€ de bienvenida para él", fr: "5€ pour vous, 10€ de bienvenue pour lui", de: "5€ für dich, 10€ Willkommen für ihn" },
  subLong: { it: "5€ di credito per te alla sua prima visita · 10€ di benvenuto per lui", en: "€5 credit for you on their first visit · €10 welcome for them", es: "5€ para ti en su primera visita · 10€ de bienvenida para él", fr: "5€ pour vous à sa première visite · 10€ de bienvenue pour lui", de: "5€ für dich beim ersten Besuch · 10€ Willkommensbonus für ihn" },
  copy: { it: "Copia link", en: "Copy link", es: "Copiar enlace", fr: "Copier le lien", de: "Link kopieren" },
  copied: { it: "Copiato!", en: "Copied!", es: "¡Copiado!", fr: "Copié !", de: "Kopiert!" },
  share: { it: "Condividi", en: "Share", es: "Compartir", fr: "Partager", de: "Teilen" },
  invited: { it: "Amici invitati", en: "Friends invited", es: "Amigos invitados", fr: "Amis invités", de: "Eingeladene Freunde" },
  none: { it: "Nessun amico ancora: condividi il tuo link!", en: "No friends yet: share your link!", es: "Aún nadie: ¡comparte tu enlace!", fr: "Personne encore : partagez votre lien !", de: "Noch niemand: teile deinen Link!" },
  pending: { it: "iscritto · in attesa della 1ª visita", en: "signed up · awaiting 1st visit", es: "inscrito · esperando 1ª visita", fr: "inscrit · en attente de la 1ère visite", de: "angemeldet · wartet auf 1. Besuch" },
  done: { it: "prima visita fatta · +5€ per te", en: "first visit done · +€5 for you", es: "primera visita hecha · +5€ para ti", fr: "première visite faite · +5€ pour vous", de: "erster Besuch · +5€ für dich" },
  earned: { it: "Guadagnati", en: "Earned", es: "Ganados", fr: "Gagnés", de: "Verdient" },
  msg: { it: "Ciao! Ti regalo 10€ di credito da Il Pozzo dei Giganti (ristorante, spa, massaggi, camere). Iscriviti qui con il mio invito:", en: "Hi! Here's €10 credit at Il Pozzo dei Giganti (restaurant, spa, massages, rooms). Sign up with my invite:", es: "¡Hola! Te regalo 10€ de crédito en Il Pozzo dei Giganti. Regístrate con mi invitación:", fr: "Salut ! Je t'offre 10€ de crédit chez Il Pozzo dei Giganti. Inscris-toi avec mon invitation :", de: "Hallo! Ich schenke dir 10€ Guthaben bei Il Pozzo dei Giganti. Melde dich mit meiner Einladung an:" },
};

export const eurFmt = (c) => `${(Number(c || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€`;

// Pill shown under the sub-navigation on the Fidelity pages (same slot as "Regala questa esperienza").
export function ReferralPill({ lang = "it" }) {
  return (
    <Link to="/fidelity-app#invita" data-testid="referral-pill" onClick={() => document.getElementById("invita")?.scrollIntoView({ behavior: "smooth", block: "start" })}
      className="group flex items-center justify-between gap-3 rounded-2xl border border-[#C48B31]/40 bg-gradient-to-r from-[#fff8ec] to-white px-4 py-3 shadow-sm hover:shadow-md hover:border-[#C48B31] transition-all">
      <span className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-[#C48B31] text-white flex items-center justify-center shadow"><Users className="h-4 w-4" /></span>
        <span className="text-left leading-tight">
          <span className="block font-serif text-base font-bold text-[#2b1410]">{tr(L.title, lang)}</span>
          <span className="block text-[11px] text-[#7c5a4e]">{tr(L.sub, lang)}</span>
        </span>
      </span>
      <span className="text-[#C48B31] group-hover:translate-x-0.5 transition-transform">→</span>
    </Link>
  );
}

export function ReferralCard({ lang = "it" }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { loyaltyReferral().then(setInfo).catch(() => {}); }, []);
  if (!info) return <div id="invita" className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>;
  const url = `${window.location.origin}/fidelity-app?ref=${info.code}`;
  const text = `${tr(L.msg, lang)} ${url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Il Pozzo dei Giganti", text: tr(L.msg, lang), url }); } catch { /* cancelled */ } }
    else copy();
  };
  return (
    <div id="invita" className="scroll-mt-24 rounded-2xl border border-[#C48B31]/40 bg-gradient-to-br from-[#fff8ec] to-white p-5 sm:p-6" data-testid="referral-card">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-full bg-[#C48B31] text-white flex items-center justify-center shadow shrink-0"><Users className="h-5 w-5" /></span>
        <div>
          <div className="font-serif text-xl font-bold text-[#2b1410]">{tr(L.title, lang)}</div>
          <div className="text-xs text-[#7c5a4e]">{tr(L.subLong, lang)}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-white border border-[#e8dcc8] px-3 py-2">
        <span className="text-[10px] uppercase tracking-widest text-[#7c5a4e]">Codice</span>
        <span className="font-mono font-bold text-lg text-[#2b1410] tracking-widest" data-testid="referral-code">{info.code}</span>
        <button onClick={copy} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#4a1c17] hover:underline" data-testid="referral-copy">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? tr(L.copied, lang) : tr(L.copy, lang)}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="referral-whatsapp"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2.5 text-sm font-semibold hover:brightness-95">WhatsApp</a>
        <button onClick={share} data-testid="referral-share"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#2b1410]"><Share2 className="h-4 w-4" /> {tr(L.share, lang)}</button>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-[#7c5a4e]">
        <span className="uppercase tracking-wide font-semibold">{tr(L.invited, lang)} · {info.invited.length}</span>
        <span>{tr(L.earned, lang)}: <b className="text-[#2b1410]" data-testid="referral-earned">{eurFmt(info.earned_cents)}</b></span>
      </div>
      {info.invited.length === 0 ? (
        <p className="mt-2 text-sm text-[#7c5a4e]" data-testid="referral-empty">{tr(L.none, lang)}</p>
      ) : (
        <ul className="mt-2 divide-y divide-[#e8dcc8] rounded-xl border border-[#e8dcc8] bg-white" data-testid="referral-list">
          {info.invited.map((r, i) => (
            <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="font-semibold text-[#2b1410]">{r.name || "Amico"}</span>
              <span className={`text-[11px] ${r.rewarded ? "text-emerald-700" : "text-[#7c5a4e]"}`}>{r.rewarded ? tr(L.done, lang) : tr(L.pending, lang)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
